import { Fragment, ReactNode } from 'react';

import { formatTimeOfDay } from 'features/common/datetimeUtils';
import {
  KM_TO_MILES,
  capitalizeFirstLetter,
  formatSpeed,
} from 'features/common/utils';
import {
  isRealtimeMetric,
  isVNRTMetric,
} from 'features/workflow_timeline/metricsOptions';

export const NA_String = 'No sample';

export function formatSegmentInfoToTableRows(
  feature,
  usesMetric,
  timeOfDay,
  timeOfDayOffset,
  timelineMetric,
) {
  function formatSpeeds(value) {
    if (!value || value.toString() === '-1') {
      return NA_String;
    }
    return formatSpeed(value * KM_TO_MILES, usesMetric);
  }

  function formatCounts(value) {
    if (!value) {
      return NA_String;
    }
    return value.toString().split('.')[0];
  }

  function indexIntoProperty(featurex, prefix) {
    const prop_key = `${prefix}${timeOfDayOffset}`;
    const value = featurex?.properties[prop_key] || featurex?.state[prop_key];
    if (!value || value === '-1') {
      return undefined;
    }
    return value.toString();
  }

  const time = formatTimeOfDay(timeOfDay);

  const counts = indexIntoProperty(feature, 'c');
  const speeds = indexIntoProperty(feature, 's');
  const typicalCounts = indexIntoProperty(feature, 'tc');
  const typicalSpeeds = indexIntoProperty(feature, 'ts');
  const rtSpeed = formatSpeeds(feature.state.rtspeed);
  const freeflowSpeed = formatSpeeds(
    feature.properties.freeflow_speed_kph ||
      feature.properties.vnrt_freeflow_kph,
  );
  const contents = [];
  if (isRealtimeMetric(timelineMetric)) {
    contents.push(
      <Fragment key="segment-rows-realtime">
        <tr>
          <td>Realtime speed</td>
          <td>{rtSpeed}</td>
        </tr>
        <tr>
          <td>Comparison speed</td>
          <td>{formatSpeeds(typicalSpeeds)}</td>
        </tr>
        <tr className="space-under">
          <td>Freeflow speed</td>
          <td>{freeflowSpeed}</td>
        </tr>
      </Fragment>,
    );
  } else if (isVNRTMetric(timelineMetric)) {
    contents.push(
      <Fragment key="segment-rows-vnrt">
        <tr>
          <td>
            <b>Speed at {time}</b>
          </td>
          <td>
            <b>{formatSpeeds(speeds)}</b>
          </td>
        </tr>
        <tr>
          <td>Comparison speed</td>
          <td>{formatSpeeds(typicalSpeeds)}</td>
        </tr>

        {rtSpeed !== NA_String ? (
          <tr>
            <td>Realtime speed</td>
            <td>{rtSpeed}</td>
          </tr>
        ) : null}
        <tr className="space-under">
          <td>Freeflow speed</td>
          <td>{freeflowSpeed}</td>
        </tr>
      </Fragment>,
    );
  } else {
    contents.push(
      <Fragment key="segment-rows-default">
        <tr>
          <td>
            <b>Speed at {time}</b>
          </td>
          <td>
            <b>{formatSpeeds(speeds)}</b>
          </td>
        </tr>
        <tr>
          <td>Comparison speed</td>
          <td>{formatSpeeds(typicalSpeeds)}</td>
        </tr>
        <tr className="space-under">
          <td>Freeflow speed</td>
          <td>{freeflowSpeed}</td>
        </tr>
        <tr>
          <td>
            <b>Trip count</b>
          </td>
          <td>
            <b>{formatCounts(counts)}</b>
          </td>
        </tr>
        <tr>
          <td>Comparison count</td>
          <td>{formatCounts(typicalCounts)}</td>
        </tr>
      </Fragment>,
    );
  }
  return contents;
}

const formattedTitleAndValue = (
  title: string,
  value: string,
  key?: string,
): ReactNode => {
  return (
    <tr key={key}>
      <td>{title}</td>
      <td>{value}</td>
    </tr>
  );
};

export function formatIncidentInfoToTableRows(incident) {
  const formattedIncidentInfo = [];
  // console.log(incident.properties);
  const incident_descriptions = incident.properties.description
    ? [incident.properties.description]
    : [];
  for (let i = 0; i < 10; i++) {
    const sub_desc = incident.properties[`description_${i}`];
    if (sub_desc) {
      incident_descriptions.push(sub_desc);
    }
  }
  if (incident_descriptions.length > 0) {
    formattedIncidentInfo.push(
      formattedTitleAndValue(
        'Incident',
        capitalizeFirstLetter(incident_descriptions.join(', ')),
        'incident-description',
      ),
    );
  }

  const { delay, alert_c_event_names } = incident.properties;

  if (alert_c_event_names) {
    formattedIncidentInfo.push(
      formattedTitleAndValue(
        'Incident',
        capitalizeFirstLetter(alert_c_event_names),
        'incident-event-names',
      ),
    );
  }

  if (delay !== undefined && delay !== 0) {
    let delay_formatted = `${delay} secs`;
    if (delay > 60) {
      delay_formatted = `${Math.floor(delay / 60)} mins`;
    }
    formattedIncidentInfo.push(
      formattedTitleAndValue('Delay', delay_formatted, 'incident-delay'),
    );
  }

  return formattedIncidentInfo;
}
